// Helper functions for dispensary location pages
// Pure functions with no side effects

import { defaultImages } from './static-data';
import { getStoreHoursOverride } from '~/utils/store-hours-override';

// Get zip code from address string
export const getZipCode = (address: string): string => {
  const match = address.match(/CA\s+(\d{5})/);
  return match ? match[1] : '00000';
};

// Calculate distance between zip codes (simplified version)
export const calculateZipDistance = (zip1: string, zip2: string): number => {
  // Simple distance calculation based on zip code numeric difference
  // This is a simplified approach - in production you'd use actual geo coordinates
  const num1 = parseInt(zip1);
  const num2 = parseInt(zip2);
  return Math.abs(num1 - num2);
};

// Get dispensary image by index with fallback
export const getDispensaryImage = (index: number): string => {
  return defaultImages[index % defaultImages.length];
};

// Create JSON-LD structured data for location
export const createLocationJsonLd = (location: any) => {
  return {
    id: `https://catalyst-cannabis.com/dispensary-locations/${location?.slug}`,
    name:
      location?.locationDetails?.headerTitle || location?.title || 'Catalyst Cannabis Dispensary',
    description: `${location?.title || 'Catalyst Cannabis'} dispensary location in California. Premium cannabis products and exceptional service.`,
    url: `https://catalyst-cannabis.com/dispensary-locations/${location?.slug}`,
    telephone: location?.locationDetails?.phone,
    email: location?.locationDetails?.email,
    image: location?.locationDetails?.storeImage?.node?.mediaItemUrl || '',
    address: {
      streetAddress: location?.locationDetails?.address,
      addressLocality: location?.title,
      addressRegion: 'CA',
      addressCountry: 'US',
    },
    openingHours: location?.locationDetails?.openingHours
      ? location.locationDetails.openingHours
          .split('\n')
          .map((hours: string) => hours.trim())
          .filter((hours: string) => hours)
      : ['Mo-Su 09:00-21:00'],
    parentOrganization: {
      name: 'Catalyst Cannabis',
      url: 'https://catalyst-cannabis.com',
    },
    priceRange: '$$',
    currenciesAccepted: 'USD',
    paymentAccepted: 'Cash, Debit Card',
  };
};

// Get gallery images with fallback
export const getGalleryImages = (locationImages: string[]): string[] => {
  return locationImages && locationImages.length > 0 ? locationImages : defaultImages;
};

// Extract location data with fallbacks
// Optionally accepts storeValue to apply date-based hour overrides (e.g., Thanksgiving, Black Friday)
export const extractLocationData = (location: any, storeValue?: string) => {
  const locationName =
    location?.locationDetails?.headerTitle || location?.title || 'Catalyst Cannabis Dispensary';
  const locationAddress = location?.locationDetails?.address || '2625 Crow Ct, Antioch, CA 94509';

  // Check for date-based hour override first (e.g., Thanksgiving, Black Friday)
  // If today is a special date and we have a store value, use override hours
  let locationHours = location?.locationDetails?.openingHours || 'DAILY - 8AM to 8PM';
  if (storeValue) {
    const override = getStoreHoursOverride(storeValue);
    if (override) {
      // Use override hours (e.g., "Open 9:00 AM – 6:00 PM")
      locationHours = override.hours;
    }
  }

  const locationPhone = location?.locationDetails?.phone || '(925) 276-9420';
  const locationEmail = location?.locationDetails?.email || 'info@catalystcannabis.co';
  const directionsUrl = location?.locationDetails?.directionsUrl || '#';
  const seoTitle = location?.locationDetails?.headerTitle || 'The Best Cannabis Dispensary';
  const mapEmbed = location?.locationDetails?.mapEmbed || '';
  const seoText =
    location?.locationDetails?.seoText || 'Default description text about the dispensary.';
  const comingSoon = Array.isArray(location?.locationDetails?.comingSoon)
    ? location.locationDetails.comingSoon
    : [];
  const imageAlt = location?.locationDetails?.storeImage?.node?.altText || '';

  return {
    locationName,
    locationAddress,
    locationHours,
    locationPhone,
    locationEmail,
    directionsUrl,
    seoTitle,
    mapEmbed,
    seoText,
    comingSoon,
    imageAlt,
  };
};
